import * as React from 'react'
import { Action, IOWidget, mk_widget } from 'widgets-for-react'
import { List } from 'immutable'
import { CMSState } from '../../../../cms/blocks/types.gen'
import { CustomBlockData } from '../types.mod'
import { ExtraImages } from '../../../utils/extra_images.mod'
import { PublicSite_CustomPageData } from '../../../application.mod'
import { PublicSiteState } from '../../../../../public_site.main.mod'
import {
  ParsedMediaDocument,
  ParsedParagraphReportsTab,
  ParsedParagraphReportsTableRow
} from '../../../../drupal/types_and_parser/types'
import { getClassName } from '../../../../cms/blocks/utils.gen'
import { reportsBlock } from '../../../../drupal/types_and_parser/custom_blocks.mod'

export type reportsBlockProps = {
  block_index: number
  field_tabs: List<ParsedParagraphReportsTab>
  current_tab: number
}

export type reportsBlockStyle = Partial<{
  block_classname: string
  inner_classname: string
  header_classname: string
  title_classname: string
  intro_classname: string
  list_classname: string
  list_item_classname: string
  list_item_label_classname: string
  item_title_classname: string
  table_head_classname: string
  table_row_classname: string
  figure_classname: string
  image_classname: string
  navigation_classname: string
  navigation_text_classname: string
  clickable_classname: string
  secondary_clickable_classname: string
  figcaption_classname: string
  caption_classname: string
}>

const isNotParsedMediaFileNull = (media?: ParsedMediaDocument): boolean =>
  media != null &&
  media.field_media_file != null &&
  media.field_media_file.file != null &&
  media.field_media_file.file.url != null

const toggleCurrentExpandable = (block_index: number, tab_index: number, row_index: number) => (
  s1: CMSState<CustomBlockData, ExtraImages, PublicSite_CustomPageData>
): CMSState<CustomBlockData, ExtraImages, PublicSite_CustomPageData> => {
  let current_block = s1.blocks.get(block_index)
  if (current_block.kind != 'reportsBlock') return s1
  let current_tab = current_block.value.fst.field_tabs.get(tab_index)
  let current_row = current_tab.field_rows.get(row_index)
  current_row = {
    ...current_row,
    //collapsed: current_row.collapsed == 'closed' ? 'open' : 'closed'
  }
  current_tab = {
    ...current_tab,
    field_rows: current_tab.field_rows.set(row_index, current_row)
  }

  current_block = {
    ...current_block,
    value: {
      ...current_block.value,
      fst: {
        ...current_block.value.fst,
        field_tabs: current_block.value.fst.field_tabs.set(tab_index, current_tab)
      }
    }
  }
  return { ...s1, blocks: s1.blocks.set(block_index, current_block) }
}

export function reportsBlock(
  props: reportsBlockProps,
  options?: { style: reportsBlockStyle; key?: string }
): (
    s: CMSState<CustomBlockData, ExtraImages, PublicSite_CustomPageData>
  ) => IOWidget<PublicSiteState, Action<CMSState<CustomBlockData, ExtraImages, PublicSite_CustomPageData>>> {
  return (s: CMSState<CustomBlockData, ExtraImages, PublicSite_CustomPageData>) => (public_state: PublicSiteState) =>
    mk_widget({
      run: (set_state) => {
        let current_tab = props.field_tabs.get(props.current_tab)
        let hasCalendar = current_tab.field_rows.reduce((xs, x) => xs || isNotParsedMediaFileNull(x.field_calendar_file), false)
        let hasReport = current_tab.field_rows.reduce((xs, x) => xs || isNotParsedMediaFileNull(x.field_report_file) || x.field_report_link != null, false)
        let hasPresentation = current_tab.field_rows.reduce((xs, x) => xs || isNotParsedMediaFileNull(x.field_presentation_file) || x.field_presentation_link != null, false)
        let hasWebcast = current_tab.field_rows.reduce((xs, x) => xs || (x.field_webcast != null && x.field_webcast.url != null), false)
        let hasBody = current_tab.field_rows.reduce((xs, x) => xs || (x.field_body != null && x.field_body != ''), false)

        return (
          <section className={getClassName(options, 'block_classname', '')}>
            <div className={getClassName(options, 'inner_classname', '')}>
              <ul
                className={`${getClassName(options, 'list_classname', '')} tabs--${props.field_tabs.size}`}
                role="tablist"
              >
                {props.field_tabs.map((tab, index) => (
                  <li
                    className={
                      getClassName(options, 'list_item_classname', '') +
                      (props.current_tab == index ? ' tab--active' : '')
                    }
                  >
                    <button
                      className={getClassName(options, 'list_item_label_classname', '')}
                      onClick={(_) => {
                        set_state((old_state) => {
                          let this_block = old_state.blocks.get(props.block_index)
                          if (this_block.kind != 'reportsBlock') return
                          this_block.value.fst.current_tab = index
                          return {
                            ...old_state,
                            blocks: old_state.blocks.set(props.block_index, this_block)
                          }
                        })
                      }}
                    >
                      <span className={getClassName(options, 'item_title_classname', '')}>{tab.field_title}</span>
                    </button>
                  </li>
                ))}
              </ul>
              <table>
                <thead className={getClassName(options, 'table_head_classname', '')}>
                  <tr className={getClassName(options, 'table_row_classname', '')}>
                    <th className="report__title report__title--date text--s">Date</th>
                    <th className="report__title report__title--details text--s">Details</th>
                    {hasCalendar && (
                      <th className="report__title report__title--download text--s">
                        Add to calendar
                      </th>)}
                    {hasReport && (
                      <th className="report__title report__title--download text--s">
                        Report
                      </th>
                    )}
                    {hasPresentation && (
                      <th className="report__title report__title--download text--s">
                        Presentation
                      </th>
                    )}
                    {hasWebcast && (
                      <th className="report__title report__title--download text--s">
                        Webcast
                      </th>
                    )}
                    {hasBody && (
                      <th className="report__title report__title--dropdown text--s"></th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {current_tab.field_rows.map((row, rowIndex) => (
                    <ReportsRow row={row} key={rowIndex} options={options}
                      hasCalendar={hasCalendar}
                      hasPresentation={hasPresentation}
                      hasReport={hasReport}
                      hasWebcast={hasWebcast}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )
      }
    })
}


interface ReportsRowProps {
  row: ParsedParagraphReportsTableRow
  hasCalendar: boolean
  hasReport: boolean
  hasPresentation: boolean
  hasWebcast: boolean
  options?: { style: reportsBlockStyle; key?: string }
}

interface ReportsRowState {
  showBody: boolean
}
class ReportsRow extends React.Component<ReportsRowProps, ReportsRowState>{
  constructor(props: ReportsRowProps) {
    super(props)
    this.state = { showBody: false }
  }

  render() {
    let options = this.props.options
    let row = this.props.row
    return <>
      <tr className={getClassName(options, 'table_row_classname', '')}>
        <td className="report__date">{row.field_date.format('DD-MM-YYYY')}</td>
        <td className="report__details">{row.field_details}</td>
        {(this.props.hasCalendar) && (
          <td className='report__calendar calendar'>
            {isNotParsedMediaFileNull(row.field_calendar_file) && row.field_calendar_file.field_media_file.file.url.endsWith('.ics') ? (
              <a
                href={row.field_calendar_file.field_media_file.file.url}
                download={true}
                target="_blank"
                aria-label="Download file"
                className="block__link block__link--calendar"
              >
                {' '}
                <svg className='calendar__icon' aria-hidden="true" height="24" width="24">
                  <use xlinkHref="#icon--calendar" />
                </svg>
              </a>
            ) : (
                ''
            )}
          </td>
        )}
        {(this.props.hasReport) && (
          <td className='report__download'>
            {isNotParsedMediaFileNull(row.field_report_file) ? (
              <a
                href={row.field_report_file.field_media_file.file.url}
                download={true}
                target="_blank"
                aria-label="Download file"
              >
                {' '}
                <svg aria-hidden="true" height="24" width="24">
                  <use xlinkHref="#icon--download-arrowdown" />
                </svg>
              </a>
            ) : row.field_report_link ? (
              <a href={row.field_report_link.url} aria-label="Download link">
                <svg aria-hidden="true" height="24" width="24">
                  <use xlinkHref="#icon--download-arrowright" />
                </svg>
              </a>
            ) : (
              ''
            )}
          </td>
        )}
        {(this.props.hasPresentation) && (
          <td className="report__download">
            {isNotParsedMediaFileNull(row.field_presentation_file) ? (
              <a
                href={row.field_presentation_file.field_media_file.file.url}
                download={true}
                target="_blank"
                aria-label="Download presentation"
              >
                <svg className="" aria-hidden="true" height="24" width="24">
                  <use xlinkHref="#icon--download-arrowdown" />
                </svg>
              </a>
            ) : row.field_presentation_link ? (
              <a href={row.field_presentation_link.url}>
                <svg aria-hidden="true" height="24" width="24">
                  <use xlinkHref="#icon--download-arrowright" />
                </svg>
              </a>
            ) : (
                  ''
            )}
          </td>
        )}
        {this.props.hasWebcast && (
          <td className="report__download">
            {row.field_webcast && row.field_webcast.url && (
              <a href={row.field_webcast.url} aria-label="Download webcast">
                <svg className="" aria-hidden="true" height="20" width="20">
                  <use xlinkHref="#icon--audio" />
                </svg>
              </a>
            )}
          </td>
        )}
        {row.field_body && (
          <td className="report__info calendar">
            <button
              className="calendar__toggler"
              onClick={(e) => {
                this.setState(s => ({ ...s, showBody: !this.state.showBody }))
                const toggle = e.currentTarget;
                if (toggle.classList.contains('calendar__toggler--open')) {
                  toggle.classList.remove('calendar__toggler--open')
                } else {
                  toggle.classList.add('calendar__toggler--open')
                }
              }
              }
            >
              <svg className="calendar__icon" height="9" width="15" aria-hidden="true">
                <use xlinkHref="#chevron--down" />
              </svg>
            </button>
          </td>
        )}
      </tr>

      {this.state.showBody && row.field_body && ( 
        <tr>
          <td className="calendar__content" colSpan={7}>
            {row.field_date && <time className="calendar__date">{row.field_date.local().format('MMMM DD, YYYY')}</time>}
            <p dangerouslySetInnerHTML={{ __html: row.field_body }} />
          </td>
        </tr>
      )}
    </>
  }
}
